import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  UserGroupIcon,
  ChartBarIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  HeartIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline'

const values = [
  {
    name: 'Customer Success First',
    description: 'Every feature we build is designed to help you find more customers and grow your business.',
    icon: HeartIcon
  },
  {
    name: 'Transparency & Trust',
    description: 'No hidden fees, no long-term contracts, no misleading promises. Just results you can measure.',
    icon: ShieldCheckIcon
  },
  {
    name: 'Innovation & Excellence',
    description: 'We\'re constantly improving our AI and developing new features to stay ahead of the competition.',
    icon: LightBulbIcon
  },
  {
    name: 'Community Impact',
    description: 'We help businesses add genuine value to Reddit communities, creating win-win relationships.',
    icon: UserGroupIcon
  }
]

const team = [
  {
    name: 'Mariusz Kobak',
    role: 'Founder',
    bio: 'Started this out of frustration with manual Reddit scrolling. Now leading a small, passionate team building the tool we wish existed.',
    emoji: '🚀'
  },
  {
    name: 'The Team',
    role: 'Builders & Dreamers',
    bio: 'We\'re three people who care deeply about making Reddit marketing easier for indie hackers and small teams. No corporate BS, just building cool stuff.',
    emoji: '💪'
  }
]

const milestones = [
  {
    year: '2023',
    title: 'The "Aha!" Moment',
    description: 'Mariusz was spending 4+ hours daily scrolling Reddit for leads. Thought "There has to be a better way!" Started coding at midnight.'
  },
  {
    year: '2025',
    title: 'Today',
    description: 'Helping indie hackers and small teams find customers on Reddit while they sleep. Growing organically, one happy customer at a time.'
  }
]

const stats = [
  { number: '200+', label: 'Happy Users' },
  { number: '10,000+', label: 'Leads Found Monthly' },
  { number: '24/7', label: 'AI Monitoring' },
  { number: '0', label: 'Reddit Bans (seriously!)' }
]

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative isolate px-6 pt-14 lg:px-8">
        <div className="mx-auto max-w-4xl py-24 sm:py-32">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"
            >
              Built by makers
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                who got tired of scrolling Reddit
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto"
            >
              We're a small, energetic team of three who built the tool we needed ourselves.
              No VC pressure. No corporate playbook. Just passionate people helping indie hackers
              and small teams find customers on Reddit.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-blue-50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-4xl font-bold text-blue-600">{stat.number}</div>
                <div className="text-lg text-gray-900 mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Origin Story */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              How It All Started
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="prose prose-lg prose-gray max-w-none"
          >
            <p className="text-lg leading-8 text-gray-700 mb-8">
              Back in 2023, our founder Mariusz was spending 4+ hours daily scrolling Reddit
              for potential customers. It worked great - Reddit was his best lead source.
              But it was exhausting and he kept missing posts while sleeping.
            </p>

            <p className="text-lg leading-8 text-gray-700 mb-8">
              One night at 2 AM, after missing another perfect opportunity, he thought:
              <strong> "Why can't AI do this boring part for me?"</strong>
            </p>

            <p className="text-lg leading-8 text-gray-700 mb-8">
              So we built the first version. It was rough, but it worked! Suddenly we were
              discovering 10x more leads without the manual grind.
            </p>

            <p className="text-lg leading-8 text-gray-700">
              Today, we're a passionate team of three helping 200+ indie makers and small teams
              find their customers on Reddit while they focus on building. No Silicon Valley hype -
              just a useful tool built by people who actually use it every day.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why We Built This</h2>
              <p className="text-lg text-gray-700 mb-6">
                Our founder Mariusz was spending hours daily scrolling Reddit for leads.
                It worked, but it was exhausting. So we built a tool to automate it.
              </p>
              <p className="text-lg text-gray-700">
                Now we're a small team of three, all using Redd Miner ourselves. We're building
                for indie makers and small teams who want quality leads without the manual grind.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What's Next?</h2>
              <p className="text-lg text-gray-700 mb-6">
                Keep improving based on real user feedback. No grand plans to "disrupt" anything
                or chase VC money. We're focused on making Redd Miner better every week.
              </p>
              <p className="text-lg text-gray-700">
                Currently working on smarter AI responses and better scheduling. Got ideas?
                We love hearing from users - seriously, every message gets read.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Company Milestones */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              The Journey
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              From midnight coding sessions to helping makers worldwide
            </p>
          </motion.div>

          <div className="space-y-12">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-6"
              >
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full font-bold">
                    {milestone.year.slice(-2)}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                  <p className="text-gray-700">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-blue-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Our Values
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-8 shadow-lg"
              >
                <div className="flex items-center mb-4">
                  <value.icon className="h-8 w-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">{value.name}</h3>
                </div>
                <p className="text-gray-700">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              The Team
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Small team, big passion - three people obsessed with making Reddit marketing effortless
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-32 h-32 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mx-auto mb-6 flex items-center justify-center text-5xl">
                  {member.emoji}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-blue-600 font-medium mb-4">{member.role}</p>
                <p className="text-gray-700">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready to Stop Scrolling Reddit Manually?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-blue-100">
              Join 200+ indie makers and small teams who let AI find their customers
              while they sleep. No credit card needed to start.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/signup"
                className="rounded-lg bg-white px-8 py-4 text-lg font-semibold text-blue-600 shadow-sm hover:bg-blue-50 transition-all duration-200 hover:scale-105"
              >
                Start Free Trial
              </Link>
              <Link to="/contact" className="text-lg font-semibold leading-6 text-white hover:text-blue-100">
                Contact Us <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AboutPage